import { useState} from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const activeCategory = categories.find((c) => c.slug === slug) || categories[0];

  const articles = getArticlesByCategory(activeCategory.id);

  const categoryTitle = `jm天堂${activeCategory.name} - 分类浏览`;
  const categoryDesc = `${activeCategory.description}。浏览jm天堂${activeCategory.name}相关文章，获取最新信息与使用帮助。`;

  return (
    <>
      <SEO
        title={categoryTitle}
        description={categoryDesc}
        keywords={`jm天堂,${activeCategory.name},jm天堂${activeCategory.name},jm天堂分类`}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '分类浏览', href: '/category/tutorial' },
            { label: activeCategory.name },
          ]}
        />

        <div className="mb-10">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            {activeCategory.name}
          </h1>
          <p className="text-foreground-600 text-base">{activeCategory.description}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-64 flex-shrink-0">
            <nav className="lg:sticky lg:top-24" aria-label="分类导航">
              <h3 className="text-sm font-semibold text-foreground-400 uppercase tracking-wider mb-4">
                全部分类
              </h3>
              <div className="space-y-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
                      cat.id === activeCategory.id
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                    }`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                      cat.id === activeCategory.id
                        ? 'bg-primary-100 text-primary-600'
                        : 'bg-background-100 text-foreground-500'
                    }`}>
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    <span>{cat.name}</span>
                    <span className="ml-auto text-xs text-foreground-400">{cat.articleCount}</span>
                  </Link>
                ))}
              </div>
            </nav>
          </aside>

          <div className="flex-1 min-w-0">
            {articles.length > 0 ? (
              <div className="space-y-5">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col sm:flex-row bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="sm:w-56 flex-shrink-0 aspect-[16/9] sm:aspect-auto overflow-hidden bg-background-200">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 p-5 flex flex-col justify-center">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full">
                          {article.categoryName}
                        </span>
                        <span className="text-xs text-foreground-500">{article.readTime}</span>
                      </div>
                      <h2 className="font-heading text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 mb-2 line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center text-xs text-foreground-500 space-x-4">
                        <span>{article.publishDate}</span>
                        <span className="flex items-center">
                          <i className="ri-eye-line mr-1"></i>
                          {article.viewCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}